// src/pages/business/BusinessConfigurationPage.jsx
import React, {useEffect, useMemo, useState} from "react";
import BusinessLayout from "../../components/BusinessLayout";
import {useAuth} from "../../context/AuthProvider";
import {
    getBusinessById,
    getBusinessConfig,
    updateBusinessConfig,
    getEmployeeByBusiness,
} from "../../services/firestoreService";

import {Timestamp} from "firebase/firestore";


const DEFAULT_CONFIG = {
    personalEnabled: true, // when in team mode, allow per-employee direct tips via their QR
    distribution: {
        mode: "INDIVIDUAL", // INDIVIDUAL | DAILY_TEAM | WEEKLY_TEAM
        basis: "EQUAL_PRESENCE", // EQUAL_PRESENCE | HOURS | HOURS_WEIGHTED
        requireManagerConfirm: true,
    },
    period: {
        type: "DAILY", // DAILY | WEEKLY (only relevant for team modes)
        cutoffHourLocal: 5,
        timezone: "Europe/Tallinn",
    },
    roles: [
        {role: "server", weight: 1.0},
        {role: "chef", weight: 1.0},
        {role: "runner", weight: 1.0},
        {role: "dishwasher", weight: 1.0},
        {role: "bartender", weight: 1.0},
    ],
    payouts: {
        schedule: "ON_CONFIRM", // ON_CONFIRM | NEXT_DAY_09:00 | WEEKLY_FRI_09:00
        roundingPolicy: "NEAREST_CENT_WITH_CORRECTION",
        descriptionTemplate:
            "Tiply – Team tips payout ({businessName}) #TiplyPayoutID-{periodKey}",
    },
    approvals: {minRoleToConfirm: "MANAGER"}, // MANAGER | OWNER
    notifications: {emailOnReady: true, emailOnPaid: true},
    version: 1,
};

const Section = ({title, children, description}) => (
    <section className="bg-white rounded-lg p-5 border">
        <div className="mb-3">
            <h2 className="text-lg font-semibold">{title}</h2>
            {description && (
                <p className="text-sm text-gray-600 leading-snug mt-1">{description}</p>
            )}
        </div>
        <div className="space-y-3">{children}</div>
    </section>
);

const RoleWeightsTable = ({roles, onChange, disabled}) => {
    const [local, setLocal] = useState(roles);

    useEffect(() => setLocal(roles), [roles]);

    const updateRow = (idx, key, val) => {
        const next = [...local];
        if (key === "weight") {
            let n = parseFloat(val);
            if (Number.isNaN(n)) n = 1.0;
            // clamp to sensible range 0.1–3.0
            n = Math.max(0.1, Math.min(3.0, n));
            next[idx][key] = parseFloat(n.toFixed(1));
        } else if (key === "role") {
            next[idx][key] = val.trim().toLowerCase();
        }
        setLocal(next);
        onChange(next);
    };

    const addRow = () => {
        const next = [...local, {role: "", weight: 1.0}];
        setLocal(next);
        onChange(next);
    };

    const removeRow = (idx) => {
        const next = local.filter((_, i) => i !== idx);
        setLocal(next);
        onChange(next);
    };

    return (
        <div>
            <div className="grid grid-cols-12 gap-2 font-medium text-sm text-gray-700 mb-2">
                <div className="col-span-7">Role</div>
                <div className="col-span-3">Weight</div>
                <div className="col-span-2 text-right">Actions</div>
            </div>
            <div className="space-y-2">
                {local.map((r, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2">
                        <input
                            className="col-span-7 p-2 border rounded-md"
                            value={r.role}
                            onChange={(e) => updateRow(idx, "role", e.target.value)}
                            placeholder="e.g. server"
                            disabled={disabled}
                        />
                        <input
                            type="number"
                            step="0.1"
                            min="0.1"
                            max="3.0"
                            className="col-span-3 p-2 border rounded-md"
                            value={r.weight}
                            onChange={(e) => updateRow(idx, "weight", e.target.value)}
                            disabled={disabled}
                        />
                        <div className="col-span-2 flex justify-end">
                            <button
                                type="button"
                                className={`px-3 py-2 border rounded-md ${
                                    disabled
                                        ? "opacity-50 cursor-not-allowed"
                                        : "hover:bg-gray-50"
                                }`}
                                onClick={() => removeRow(idx)}
                                disabled={disabled}
                            >
                                Remove
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            <button
                type="button"
                className={`mt-3 px-3 py-2 border rounded-md ${
                    disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-50"
                }`}
                onClick={addRow}
                disabled={disabled}
            >
                Add Role
            </button>
            <p className="text-xs text-gray-500 mt-2">
                Tip: keep weights around 0.3–1.5. Common defaults: server 1.0, chef
                0.5, runner 0.3, dishwasher 0.3, bartender 1.0.
            </p>
        </div>
    );
};

const BusinessConfigurationPage = () => {
    const {userData, currentUser} = useAuth();
    const [business, setBusiness] = useState(null);
    const [config, setConfig] = useState(null);
    const [saving, setSaving] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [showEmployees, setShowEmployees] = useState(false); // optional toggle

    // Load business + config
    useEffect(() => {
        const load = async () => {
            const biz = await getBusinessById(userData.businessID);
            setBusiness(biz);

            let cfg = await getBusinessConfig(userData.businessID);
            if (!cfg) {
                cfg = DEFAULT_CONFIG;
            } else {
                // backfill any missing keys (forward compatibility)
                cfg = {
                    ...DEFAULT_CONFIG,
                    ...cfg,
                    distribution: {...DEFAULT_CONFIG.distribution, ...cfg.distribution},
                    period: {...DEFAULT_CONFIG.period, ...cfg.period},
                    payouts: {...DEFAULT_CONFIG.payouts, ...cfg.payouts},
                    approvals: {...DEFAULT_CONFIG.approvals, ...cfg.approvals},
                    notifications: {
                        ...DEFAULT_CONFIG.notifications,
                        ...cfg.notifications,
                    },
                    roles: cfg.roles?.length ? cfg.roles : DEFAULT_CONFIG.roles,
                    personalEnabled: typeof cfg.personalEnabled === "boolean" ? cfg.personalEnabled : DEFAULT_CONFIG.personalEnabled,
                };
            }
            setConfig(cfg);

            // TODO we should also add a personalEnabled flag and teamEnabled (teamEnabled is automatically set if they choose Team splits)
            // TODO personaEnabled should have I guess a separate button or a check mark with explanation
            // TODO after we should also think about what happens if they suddenly uncheck something or idk you know
            // TODO tbh we payout based on the configuration and when we start payout, we fetched latest configuration and then pay out
            // TODO so if they changed something, that's on them

            // (optional) preload employees for quick role visualization/edit

            // const emps = await listEmployeesByBusiness(userData.businessID);
            const emps = await getEmployeeByBusiness(userData.businessID);
            setEmployees(emps || []);

        };
        load();
    }, [userData]);

    const isTeamMode =
        config?.distribution.mode === "DAILY_TEAM" ||
        config?.distribution.mode === "WEEKLY_TEAM";

    // Derived flags that UI (and TipPage) care about:
    const personalEnabled = config?.distribution.mode === "INDIVIDUAL"
        ? true
        : !!config?.personalEnabled;

    const periodKeyPreview = useMemo(() => {
        if (!config) return "";
        if (config.distribution.mode === "INDIVIDUAL") return "N/A";
        if (config.period.type === "DAILY") return "YYYY-MM-DD-D";
        if (config.period.type === "WEEKLY") return "YYYY-Www";
        return "PERIOD";
    }, [config]);

    const descriptionPreview = useMemo(() => {
        if (!config || !business) return "";
        return config.payouts.descriptionTemplate
            .replace("{businessName}", business.displayName || business.legalName)
            .replace("{periodKey}", periodKeyPreview);
    }, [config, business, periodKeyPreview]);

    const setCfg = (path, value) => {
        setConfig((prev) => {
            const next = structuredClone(prev);
            const keys = path.split(".");
            let obj = next;
            for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
            obj[keys[keys.length - 1]] = value;
            return next;
        });
    };

    const handleSave = async () => {
        setSaving(true);
        // basic validation
        if (isTeamMode && config.period.cutoffHourLocal >= 24) {
            alert("Cutoff hour must be between 0 and 23.");
            setSaving(false);
            return;
        }
        // normalize roles
        const roles = (config.roles || [])
            .map((r) => ({
                role: (r.role || "").trim().toLowerCase(),
                weight: Number.isFinite(r.weight) ? r.weight : 1.0,
            }))
            .filter((r) => r.role);

        const derivedTeamEnabled =
            config.distribution.mode === "DAILY_TEAM" ||
            config.distribution.mode === "WEEKLY_TEAM";
        const derivedPersonalEnabled =
            config.distribution.mode === "INDIVIDUAL" ? true : !!config.personalEnabled;

        const payload = {
            ...config,
            personalEnabled: derivedPersonalEnabled,
            roles,
            version: (config.version || 0) + 1,
            updatedAt: Timestamp.now(),
            updatedBy: currentUser.uid,
            // These are convenient to keep at root (mirrors what public needs)
            teamEnabled: derivedTeamEnabled,
        };

        await updateBusinessConfig(userData.businessID, payload);
        setSaving(false);
        alert("Configuration saved.");
    };

    if (!config || !business) {
        return (
            <BusinessLayout>
                <div className="max-w-5xl mx-auto py-8">
                    <div className="p-8 text-center">Loading configuration…</div>
                </div>
            </BusinessLayout>
        );
    }

    if (business.onboardingStatus) {
        return (
            <BusinessLayout>
                <div className="max-w-3xl mx-auto py-8">
                    <div className="rounded-2xl border border-emerald-200 bg-white p-7 shadow-sm">
                        <p className="text-xs font-bold uppercase tracking-wider text-[#0d7c5f]">Tip distribution</p>
                        <h1 className="mt-2 text-2xl font-bold">Individual tipping is enabled</h1>
                        <p className="mt-3 text-gray-600">Each active employee receives tips through their own Tiply link. Self-service team distribution is not part of the current onboarding release.</p>
                        <a href="mailto:team@tiply.ee?subject=Tiply%20team%20tipping" className="mt-6 inline-flex rounded-xl bg-[#0d7c5f] px-5 py-3 font-semibold text-white">Ask about shift/hour-weighted team tipping</a>
                    </div>
                </div>
            </BusinessLayout>
        );
    }

    return (
        <BusinessLayout>
            <div className="max-w-5xl mx-auto py-8 space-y-6">
                <h1 className="text-2xl font-bold">Business Configuration</h1>

                <Section
                    title="Tip Distribution"
                    description="Choose how tips are split. Individual means each employee’s QR pays them directly. Team modes pool tips and split by day or by week."
                >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                            <label className="block text-sm text-gray-700 mb-1">
                                Mode
                            </label>
                            <select
                                className="w-full p-2 border rounded-md"
                                value={config.distribution.mode}
                                onChange={(e) => {
                                    const mode = e.target.value;
                                    setCfg("distribution.mode", mode);
                                    if (mode === "INDIVIDUAL") {
                                        setCfg("distribution.basis", "EQUAL_PRESENCE");
                                        // In INDIVIDUAL mode, personal is always enabled and team is off
                                        setCfg("personalEnabled", true);
                                    }
                                }}
                            >
                                <option value="INDIVIDUAL">Individual (per-employee QR)</option>
                                <option value="DAILY_TEAM">Team (Daily split)</option>
                                <option value="WEEKLY_TEAM">Team (Weekly split)</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm text-gray-700 mb-1">
                                Basis
                            </label>
                            <select
                                className="w-full p-2 border rounded-md"
                                value={config.distribution.basis}
                                onChange={(e) => setCfg("distribution.basis", e.target.value)}
                                disabled={!isTeamMode}
                            >
                                <option value="EQUAL_PRESENCE">Equal presence (simple)</option>
                                <option value="HOURS">By hours worked</option>
                                <option value="HOURS_WEIGHTED">Hours × Role weight</option>
                            </select>
                            {!isTeamMode && (
                                <p className="text-xs text-gray-500 mt-1">
                                    Basis applies only to team modes.
                                </p>
                            )}
                        </div>

                        <div className="flex items-end">
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={config.distribution.requireManagerConfirm}
                                    onChange={(e) =>
                                        setCfg("distribution.requireManagerConfirm", e.target.checked)
                                    }
                                    disabled={!isTeamMode}
                                />
                                Require manager confirmation before payouts
                            </label>
                        </div>

                        {/* Personal tips toggle (visible only in team modes) */}
                        {isTeamMode ? (
                        <div className="md:col-span-3">
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={personalEnabled}
                                    onChange={(e) => setCfg("personalEnabled", e.target.checked)}
                                />
                                Allow individual direct tips alongside team pool
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                                If enabled, scanning an employee’s QR pays that person directly. Scanning the team QR
                                still goes to the pool.
                            </p>
                        </div>
                    ) : (
                        <div className="md:col-span-3 text-xs text-gray-500">
                            In Individual mode, personal tips are always enabled and team pool is disabled.
                        </div>
                    )}
                    </div>
                </Section>

                <Section
                    title="Period & Cutoff"
                    description="Define the service day and cutoff hour for late-night shifts. Anything between the cutoff today and the next cutoff belongs to the same service day."
                >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                            <label className="block text-sm text-gray-700 mb-1">
                                Period
                            </label>
                            <select
                                className="w-full p-2 border rounded-md"
                                value={config.period.type}
                                onChange={(e) => setCfg("period.type", e.target.value)}
                                disabled={!isTeamMode}
                            >
                                <option value="DAILY">Daily</option>
                                <option value="WEEKLY">Weekly</option>
                            </select>
                            {!isTeamMode && (
                                <p className="text-xs text-gray-500 mt-1">
                                    Period applies only to team modes.
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm text-gray-700 mb-1">
                                Service day cutoff (local hour)
                            </label>
                            <input
                                type="number"
                                min={0}
                                max={23}
                                className="w-full p-2 border rounded-md"
                                value={config.period.cutoffHourLocal}
                                onChange={(e) =>
                                    setCfg("period.cutoffHourLocal", parseInt(e.target.value || "0", 10))
                                }
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Recommended: <b>5</b> — groups 11:00–02:00 nights into one day.
                            </p>
                        </div>

                        <div>
                            <label className="block text-sm text-gray-700 mb-1">Timezone</label>
                            <input
                                className="w-full p-2 border rounded-md bg-gray-100"
                                value={config.period.timezone || "Europe/Tallinn"}
                                readOnly
                            />
                        </div>
                    </div>
                </Section>

                <Section
                    title="Role Weights"
                    description="Weights are used only if the basis is ‘Hours × Role weight’. Keep it simple at first; you can refine later."
                >
                    <RoleWeightsTable
                        roles={config.roles}
                        onChange={(next) => setCfg("roles", next)}
                        disabled={config.distribution.basis !== "HOURS_WEIGHTED"}
                    />
                </Section>

                <Section
                    title="Payouts"
                    description="Choose when payouts are executed and review the bank description preview."
                >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                            <label className="block text-sm text-gray-700 mb-1">
                                Schedule
                            </label>
                            <select
                                className="w-full p-2 border rounded-md"
                                value={config.payouts.schedule}
                                onChange={(e) => setCfg("payouts.schedule", e.target.value)}
                            >
                                <option value="ON_CONFIRM">Immediately on confirm</option>
                                <option value="NEXT_DAY_09:00">Next day at 09:00</option>
                                <option value="WEEKLY_FRI_09:00">Weekly (Fri 09:00)</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm text-gray-700 mb-1">
                                Rounding policy
                            </label>
                            <input
                                className="w-full p-2 border rounded-md bg-gray-100"
                                value={config.payouts.roundingPolicy}
                                readOnly
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                We add a 1-cent adjustment to ensure sums match exactly.
                            </p>
                        </div>

                        <div>
                            <label className="block text-sm text-gray-700 mb-1">
                                Description template (read-only)
                            </label>
                            <input
                                className="w-full p-2 border rounded-md bg-gray-100"
                                value={config.payouts.descriptionTemplate}
                                readOnly
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Placeholders: {"{businessName}"} and {"{periodKey}"}.
                            </p>
                        </div>
                    </div>

                    <div className="mt-2">
                        <label className="block text-sm text-gray-700 mb-1">
                            Bank description preview
                        </label>
                        <div className="p-3 border rounded-md bg-gray-50 text-sm font-mono">
                            {descriptionPreview}
                        </div>
                    </div>
                </Section>

                <Section
                    title="Approvals & Notifications"
                    description="Control who can confirm payouts and what email notifications are sent."
                >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                            <label className="block text-sm text-gray-700 mb-1">
                                Minimum role to confirm
                            </label>
                            <select
                                className="w-full p-2 border rounded-md"
                                value={config.approvals.minRoleToConfirm}
                                onChange={(e) => setCfg("approvals.minRoleToConfirm", e.target.value)}
                            >
                                <option value="MANAGER">Manager</option>
                                <option value="OWNER">Owner</option>
                            </select>
                        </div>

                        <div className="flex items-end">
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={config.notifications.emailOnReady}
                                    onChange={(e) =>
                                        setCfg("notifications.emailOnReady", e.target.checked)
                                    }
                                />
                                Email when a period is ready to confirm
                            </label>
                        </div>

                        <div className="flex items-end">
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={config.notifications.emailOnPaid}
                                    onChange={(e) =>
                                        setCfg("notifications.emailOnPaid", e.target.checked)
                                    }
                                />
                                Email when payouts are sent
                            </label>
                        </div>
                    </div>
                </Section>

                {/* OPTIONAL: quick employee list (roles only). Toggle on if you want it now. */}
                {showEmployees && (
                    <Section
                        title="Employees (quick view)"
                        description="Role here is for display; manage full employee details in the Employees page."
                    >
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead>
                                <tr className="text-left text-gray-700">
                                    <th className="py-2 pr-3">Name</th>
                                    <th className="py-2 pr-3">Email</th>
                                    <th className="py-2 pr-3">Role</th>
                                    <th className="py-2 pr-3">IBAN</th>
                                </tr>
                                </thead>
                                <tbody>
                                {employees.map((e) => (
                                    <tr key={e.id} className="border-t">
                                        <td className="py-2 pr-3">{e.firstName || "—"}</td>
                                        <td className="py-2 pr-3">{e.email || "—"}</td>
                                        <td className="py-2 pr-3 capitalize">{e.role || "—"}</td>
                                        <td className="py-2 pr-3 font-mono">{e.iban || "—"}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </Section>
                )}

                <div className="flex gap-3">
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className={`px-4 py-2 rounded-md font-semibold ${
                            saving
                                ? "bg-gray-400 text-white cursor-not-allowed"
                                : "bg-black text-white hover:bg-neutral-800"
                        }`}
                    >
                        {saving ? "Saving..." : "Save Configuration"}
                    </button>

                    <button
                        type="button"
                        className="px-4 py-2 rounded-md border hover:bg-gray-50"
                        onClick={() => setShowEmployees((v) => !v)}
                    >
                        {showEmployees ? "Hide Employees" : "Show Employees"}
                    </button>
                </div>

                <div
                    className="bg-yellow-50 border border-yellow-300 text-yellow-800 text-sm px-4 py-3 rounded-md shadow-sm">
                    Tip: Start with <b>Individual</b> or <b>Team (Daily) – Equal presence</b>.
                    You can switch to hours or role weights later.
                </div>
            </div>
        </BusinessLayout>
    );
};

export default BusinessConfigurationPage;
